import {
    ActionType,
    GetState,
    ReceiveSelectedObjectAction,
    VOAbortedAction,
    VOFailedAction,
    VOFetchAction,
    VOReceivedAction,
} from '@/actions/actiontypes';
import { handleError } from '@/actions/metaActions';
import { Data } from '@/bootstrap/data';
import { Fylke, Kommune } from '@/domain/omrader';
import { Egenskapstype, EgenskapTillattVerdi } from '@/domain/vegobjekter/Egenskapstype';
import { Vegobjekt, vegobjektVersionsComparator } from '@/domain/vegobjekter/Vegobjekt';
import {
    VegobjekterResult,
    VegobjekterStatistic,
} from '@/domain/vegobjekter/VegobjekterResult';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import {
    stateMap,
    stateOmrader,
    stateVegnett,
    stateVegobjekter,
} from '@/selectors/selectors';
import { ApiError, AuthenticationError, isRequestAborted } from '@/server/server';
import {
    ApiVersion,
    CategoryType,
    MapState,
    OmraderState,
    Severity,
    ValgtState,
    VegkartError,
    VegnettState,
    VegobjekterState,
    VegobjekttypeCategoryState,
    VegobjekttypeState,
    VegobjekttypeStateError,
} from '@/state';
import { Dispatch } from '@/state/store';
import { ColorIndex } from '@/utils/ColorHelper';
import { resolveInitialCategoryColor } from '@/utils/ColorPreferences';
import { createLogger } from '@/utils/Logger';
import { getApiVersion, numericSortComparator } from '@/utils/Utils';
import * as selectors from '../selectors/selectors';
import { getKommunerForQuery, getFylkerForQuery } from './fetchHelpers';
import { Integration } from './integration';
import {
    CommonQueries,
    Egenskapsfilter,
    MapQueries,
    OmraderQueries,
    OperatorE,
    VegnettQueries,
    VegobjekterQueries,
} from './queries';
import { geometryTolerance } from './fetchHelpers';

const log = createLogger('fetchVegobjekter');
const activeVegobjekterRequestControllers = new Map<number | null, AbortController>();

export function abortActiveVegobjekterRequestsV4(): void {
    for (const controller of activeVegobjekterRequestControllers.values()) {
        controller.abort();
    }
    activeVegobjekterRequestControllers.clear();
}

function beginVegobjekterRequest(typeId: number | null): AbortController {
    activeVegobjekterRequestControllers.get(typeId)?.abort();
    const controller = new AbortController();
    activeVegobjekterRequestControllers.set(typeId, controller);
    return controller;
}

function finishVegobjekterRequest(
    typeId: number | null,
    controller: AbortController
): void {
    if (activeVegobjekterRequestControllers.get(typeId) === controller) {
        activeVegobjekterRequestControllers.delete(typeId);
    }
}

export const fetchSelectedVegobjektV4 =
    (valgtState: ValgtState) =>
    async (dispatch: Dispatch, getState: GetState, integration: Integration) => {
        const fetchCompleteVegobjekt = async (vegobjekt: Vegobjekt) => {
            const type = vegobjekt.metadata.type.id;
            const id = vegobjekt.id;
            const version = vegobjekt.metadata.version;
            return integration.server.getVegobjekt(id, undefined, type, version);
        };

        try {
            const completed = await Promise.all(
                valgtState.vegobjekter.map(fetchCompleteVegobjekt)
            );
            const receivedSelectedAction: ReceiveSelectedObjectAction = {
                type: ActionType.VALGT_OBJEKT_RECEIVED,
                selection: valgtState
                    .withFetching(false)
                    .withVegobjekter(completed.filter(v => !!v)),
            };
            dispatch(receivedSelectedAction);
        } catch (e) {
            dispatch(
                handleError(
                    new VegkartError('Kunne ikke motta vegobjekt', {
                        e,
                        vegobjekt: valgtState.vegobjekter,
                    })
                )
            );
        }
    };

export const fetchVegobjektV4 =
    (nvdbId: number) =>
    (dispatch: Dispatch, getState: GetState, integration: Integration) => {
        log('fetchVegobjekt {}', nvdbId);
        const timestamp =
            getState().searchState.searchDropdownState.timestamp || new Date();
        return integration.server.getVegobjekt(nvdbId).then(
            (vegobjekt: Vegobjekt) => {
                log('receiveVegobjekt {}', vegobjekt.id);
                if (new Date(vegobjekt.metadata.startdato) <= timestamp) {
                    const receivedSelectedAction: ReceiveSelectedObjectAction = {
                        type: ActionType.VALGT_OBJEKT_RECEIVED,
                        selection: ValgtState.vegobjektSelection(vegobjekt),
                    };
                    dispatch(receivedSelectedAction);
                } else {
                    integration.server
                        .getVegobjektVersionsMinimal(
                            vegobjekt.id,
                            vegobjekt.metadata.type.id
                        )
                        .then((vegobjekterFromServer: Vegobjekt[]) => {
                            integration.server
                                .getVegobjekt(
                                    vegobjekt.id,
                                    geometryTolerance(getState().mapState.zoom),
                                    vegobjekt.metadata.type.id,
                                    vegobjekterFromServer
                                        .filter(
                                            v =>
                                                new Date(v.metadata.startdato) <=
                                                timestamp
                                        )
                                        .sort(vegobjektVersionsComparator)
                                        .pop()?.metadata.version
                                )
                                .then((vegobjektFromServer: Vegobjekt) => {
                                    const receivedSelectedAction: ReceiveSelectedObjectAction =
                                        {
                                            type: ActionType.VALGT_OBJEKT_RECEIVED,
                                            selection:
                                                ValgtState.vegobjektSelection(
                                                    vegobjektFromServer
                                                ),
                                        };
                                    dispatch(receivedSelectedAction);
                                });
                        });
                }
            },
            err =>
                dispatch(
                    handleError(
                        new VegkartError('Kunne ikke motta vegobjekt', {
                            err,
                            vegobjekttype: nvdbId,
                        })
                    )
                )
        );
    };

export const executeAllSearchesV4 = () => (dispatch: Dispatch, getState: GetState) => {
    log('executeAllSearches');
    const typesInSearch = selectors.stateSelectedRoadObjectTypes(getState());

    typesInSearch.forEach(type => {
        dispatch(fetchVegobjekterV4(type));
    });
};

function mapError(e: unknown): VegobjekttypeStateError {
    if (e instanceof AuthenticationError) return 'auth';
    else if (e instanceof ApiError && e.msg.code === 4015) return 'not-indexed';
    else if (e instanceof ApiError) return 'api';
    else return 'unknown';
}

export const fetchVegobjekterV4 =
    (votState: VegobjekttypeState) =>
    async (dispatch: Dispatch, getState: GetState, integration: Integration) => {
        const mapState: MapState = selectors.stateMap(getState());
        const omraderState: OmraderState = selectors.stateOmrader(getState());
        const vegnettState: VegnettState = selectors.stateVegnett(getState());
        const vegobjekterState: VegobjekterState = selectors.stateVegobjekter(getState());
        const typeId = votState.typeId;
        if (typeId === null) return;
        const resolvedTypeId: number = typeId;
        const typeHasCategory = votState.category !== null && votState.categoryIsValid();
        const staticData: Data = integration.data;
        const vegobjekttype: Vegobjekttype =
            integration.data.datakatalog.getTypeById(resolvedTypeId);

        // Query classes
        const commonQueries: CommonQueries = new CommonQueries().withGeometritoleranse(
            geometryTolerance(mapState.zoom) ?? null
        );
        const mapQueries: MapQueries = mapState.toQueries();
        const omraderQueries: OmraderQueries = omraderState.toOmradeQueries(
            staticData.kommuner
        );
        const vegnettQueries: VegnettQueries = vegnettState.filter;
        const vegobjekterQueries: VegobjekterQueries =
            vegobjekterState.typeToQueries(resolvedTypeId);

        // Timestamp
        const timestamp = getState().searchState.searchDropdownState.timestamp;

        function shouldCancel() {
            return hasSearchStateChanged(
                resolvedTypeId,
                mapQueries,
                omraderQueries,
                vegnettQueries,
                vegobjekterQueries,
                getState,
                staticData.kommuner
            );
        }

        function cancel(): void {
            const type = ActionType.VEGOBJEKTER_FETCH_ABORTED;
            const timeAfterFetch = new Date().getTime();
            integration.server.postPerformanceLogEntry(
                type,
                timeAfterFetch - timeBeforeFetch,
                {
                    vegobjekttype: resolvedTypeId,
                }
            );
            const action: VOAbortedAction = { type };
            dispatch(action);
        }

        // Dispatch fetching action for animated loader-icons
        const fetchVegobjekterAction: VOFetchAction = {
            type: ActionType.VEGOBJEKTER_FETCH_INITIATED,
            votTypeId: resolvedTypeId,
        };
        dispatch(fetchVegobjekterAction);
        const timeBeforeFetch = new Date().getTime();
        const requestController = beginVegobjekterRequest(typeId);
        const signal = requestController.signal;

        try {
            // Fetch all relevant statistics.
            let totalStatistics: VegobjekterStatistic | null = null;
            let extentStatistics: VegobjekterStatistic | null = null;
            const totalStatsPromise =
                omraderQueries.areasLength() > 0
                    ? fetchVegobjekterStatisticsV4(
                          resolvedTypeId,
                          new MapQueries(),
                          omraderQueries,
                          vegnettQueries,
                          vegobjekterQueries,
                          integration,
                          undefined,
                          [],
                          timestamp,
                          signal
                      )
                    : Promise.resolve(null);
            const extentStatsPromise = fetchVegobjekterStatisticsV4(
                resolvedTypeId,
                mapQueries,
                omraderQueries,
                vegnettQueries,
                vegobjekterQueries,
                integration,
                undefined,
                [],
                timestamp,
                signal
            );
            [totalStatistics, extentStatistics] = await Promise.all([
                totalStatsPromise,
                extentStatsPromise,
            ]);
            if (shouldCancel()) return cancel();

            // Based on totals and geometry type, determine if we just display statistics or the actual objects.
            const pointLimit = integration.config.maximum_number_of_objects_point;
            const lineLimit = integration.config.maximum_number_of_objects_line;
            let vegobjekterResult: VegobjekterResult | null = null;
            let probeResult: RoadSysRefAndTotalT | null = null;
            let areaStatistics: VegobjekterStatistic[] = [];

            const isOverPointLimit =
                vegobjekttype.isPoint() && extentStatistics!.antall > pointLimit;
            const isOverLineLimit =
                vegobjekttype.isLine() && extentStatistics!.antall > lineLimit;

            if (omraderState.polygon?.visible && (isOverPointLimit || isOverLineLimit)) {
                const abortFetch: VOFailedAction = {
                    type: ActionType.VEGOBJEKTER_FETCH_FAILED,
                    votTypeId: resolvedTypeId,
                    error: 'polygon-limit',
                };

                dispatch(abortFetch);

                return;
            }

            const createStatisticsOnlyResult = async () => {
                areaStatistics = await fetchVegobjekterStatisticByArea(
                    resolvedTypeId,
                    mapQueries,
                    omraderQueries,
                    vegnettQueries,
                    vegobjekterQueries,
                    integration,
                    staticData.fylker,
                    staticData.kommuner,
                    timestamp,
                    mapState.zoom,
                    signal
                );

                if (shouldCancel()) return cancel();
                vegobjekterResult = new VegobjekterResult(
                    [],
                    totalStatistics ?? undefined,
                    extentStatistics!,
                    areaStatistics
                );
                const receivedVegobjekterAction: VOReceivedAction = {
                    type: ActionType.VEGOBJEKTER_FETCH_RECEIVED,
                    votTypeId: typeId,
                    result: vegobjekterResult,
                    categoryStates: [],
                };
                dispatch(receivedVegobjekterAction);

                const timeAfterFetch = new Date().getTime();
                integration.server.postPerformanceLogEntry(
                    ActionType.VEGOBJEKTER_FETCH_RECEIVED,
                    timeAfterFetch - timeBeforeFetch,
                    {
                        vegobjekttype: typeId,
                    }
                );
                return;
            };

            if (
                mapState.zoom < 8 &&
                [580 /*Kontraktsområde*/, 704 /*Riksvegrute*/].includes(resolvedTypeId)
            ) {
                return await createStatisticsOnlyResult();
            }

            if (isOverPointLimit || isOverLineLimit) {
                if (omraderQueries.vegsystemreferanse.length > 0) {
                    // If we already filter by vegsystemreferanse, we just return statistics.
                    return await createStatisticsOnlyResult();
                }

                probeResult = await getRoadSysRefAndTotalFromProbeResult(
                    resolvedTypeId,
                    mapQueries,
                    omraderQueries,
                    integration,
                    signal
                );

                if (shouldCancel()) return cancel();

                if (probeResult.statistic.antall > lineLimit) {
                    return await createStatisticsOnlyResult();
                } else {
                    totalStatistics = extentStatistics;
                    extentStatistics = probeResult.statistic;
                }
            }

            // Finally, we fetch the objects.
            if (typeHasCategory && votState.category !== null) {
                const categoryStatesResponse = await fetchVegobjekterByCategories(
                    votState,
                    commonQueries,
                    mapQueries,
                    omraderQueries,
                    vegnettQueries,
                    vegobjekterQueries,
                    integration,
                    getState,
                    probeResult?.roadSysRefs ?? [],
                    timeBeforeFetch,
                    signal
                );
                if (shouldCancel()) return cancel();
                vegobjekterResult = new VegobjekterResult(
                    [],
                    totalStatistics ?? undefined,
                    extentStatistics,
                    areaStatistics
                );
                const receivedVegobjekterAction: VOReceivedAction = {
                    type: ActionType.VEGOBJEKTER_FETCH_RECEIVED,
                    votTypeId: resolvedTypeId,
                    result: vegobjekterResult,
                    categoryStates: categoryStatesResponse,
                };
                dispatch(receivedVegobjekterAction);

                const timeAfterFetch = new Date().getTime();
                integration.server.postPerformanceLogEntry(
                    ActionType.VEGOBJEKTER_FETCH_RECEIVED,
                    timeAfterFetch - timeBeforeFetch,
                    {
                        vegobjekttype: typeId,
                    }
                );
            } else {
                const vegobjekterResponse = await fetchVegobjekterByOmradeType(
                    resolvedTypeId,
                    commonQueries,
                    mapQueries,
                    omraderQueries,
                    vegnettQueries,
                    vegobjekterQueries,
                    integration,
                    getState,
                    null,
                    probeResult?.roadSysRefs ?? [],
                    timeBeforeFetch,
                    signal
                );
                if (shouldCancel()) return cancel();
                vegobjekterResult = new VegobjekterResult(
                    vegobjekterResponse,
                    totalStatistics ?? undefined,
                    extentStatistics,
                    areaStatistics
                );
                const receivedVegobjekterAction: VOReceivedAction = {
                    type: ActionType.VEGOBJEKTER_FETCH_RECEIVED,
                    votTypeId: resolvedTypeId,
                    result: vegobjekterResult,
                    categoryStates: [],
                };
                dispatch(receivedVegobjekterAction);

                const timeAfterFetch = new Date().getTime();
                integration.server.postPerformanceLogEntry(
                    ActionType.VEGOBJEKTER_FETCH_RECEIVED,
                    timeAfterFetch - timeBeforeFetch,
                    {
                        vegobjekttype: resolvedTypeId,
                    }
                );
            }
        } catch (e) {
            if (isRequestAborted(e) || shouldCancel()) return cancel();
            const error = mapError(e);
            console.log(e);
            const action: VOFailedAction = {
                type: ActionType.VEGOBJEKTER_FETCH_FAILED,
                votTypeId: typeId,
                error,
            };
            return dispatch(action);
        } finally {
            finishVegobjekterRequest(resolvedTypeId, requestController);
        }
    };

interface RoadSysRefAndTotalT {
    roadSysRefs: string[];
    statistic: VegobjekterStatistic;
}

async function getRoadSysRefAndTotalFromProbeResult(
    typeId: number,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    integration: Integration,
    signal?: AbortSignal
): Promise<RoadSysRefAndTotalT> {
    const roadRefRequestAll = ['E', 'R', 'F', 'K', 'P', 'S'];
    const roadRefRequest = [...roadRefRequestAll];
    const roadRefResult = new Map<string, VegobjekterStatistic>();

    if (omraderQueries.areasLength() > 0) {
        await integration.server
            .getAllVegobjekterStatisticsGruppert(
                typeId,
                mapQueries.kartutsnitt ?? undefined,
                signal
            )
            .then(res => {
                res.forEach(r => {
                    if (
                        r.vegkategori &&
                        ((omraderQueries.fylke.length == 0 &&
                            omraderQueries.kommune.length == 0) ||
                            omraderQueries.kommune.includes(r.kommune.toString()) ||
                            omraderQueries.fylke.includes(r.fylke.toString()))
                    ) {
                        const existing = roadRefResult.get(r.vegkategori);
                        if (existing) {
                            roadRefResult.set(r.vegkategori, {
                                antall: existing.antall + r.antall,
                                lengde: existing.lengde + r.lengde,
                            });
                        } else {
                            roadRefResult.set(r.vegkategori, {
                                antall: r.antall,
                                lengde: r.lengde,
                            });
                        }
                    }
                });
            });
    } else {
        await integration.server
            .getVegobjekterStatisticsByKategoriGruppert(
                typeId,
                mapQueries.kartutsnitt ?? undefined,
                signal
            )
            .then(res => {
                res.forEach(r => {
                    if (roadRefRequestAll.includes(r.vegkategori)) {
                        if (r.vegkategori) {
                            const existing = roadRefResult.get(r.vegkategori);
                            if (existing) {
                                roadRefResult.set(r.vegkategori, {
                                    antall: existing.antall + r.antall,
                                    lengde: existing.lengde + r.lengde,
                                });
                            } else {
                                roadRefResult.set(r.vegkategori, {
                                    antall: r.antall,
                                    lengde: r.lengde,
                                });
                            }
                        }
                    }
                });
            });
    }

    const totalAll = getTotal(roadRefResult);
    let total = totalAll;

    while (
        total.antall > integration.config.maximum_number_of_objects_line &&
        roadRefResult.size > 0
    ) {
        const lastRef = roadRefRequest.pop();
        if (lastRef) {
            roadRefResult.delete(lastRef);
        }
        total = getTotal(roadRefResult);
    }

    return roadRefResult.size > 0
        ? { roadSysRefs: roadRefRequest, statistic: total }
        : { roadSysRefs: roadRefRequestAll, statistic: totalAll };
}

function getTotal(map: Map<string, VegobjekterStatistic>): VegobjekterStatistic {
    let antall = 0;
    let lengde = 0;
    for (const value of map.values()) {
        antall += value.antall;
        lengde += value.lengde;
    }
    return { antall, lengde };
}

const fetchVegobjekterStatisticByArea = async (
    typeId: number,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    vegobjekterQueries: VegobjekterQueries,
    integration: Integration,
    fylker: Fylke[],
    kommuner: Kommune[],
    timestamp: Date | null,
    zoom: number,
    signal?: AbortSignal
): Promise<VegobjekterStatistic[]> => {
    const results: VegobjekterStatistic[] = [];

    if (zoom < 6) {
        await integration.server
            .getVegobjekterStatisticsGruppertByFylke(
                typeId,
                mapQueries,
                omraderQueries,
                vegnettQueries,
                vegobjekterQueries,
                timestamp,
                signal
            )
            .then(res => {
                res.forEach(r => {
                    const fylke = getFylkerForQuery(omraderQueries, fylker)?.find(
                        f => f?.nummer === r?.fylke
                    );
                    if (fylke) {
                        results.push(new VegobjekterStatistic(r.antall, r.lengde, fylke));
                    }
                });
            });
    } else {
        await integration.server
            .getVegobjekterStatisticsGruppertByKommune(
                typeId,
                mapQueries,
                omraderQueries,
                vegnettQueries,
                vegobjekterQueries,
                timestamp,
                signal
            )
            .then(res => {
                res.forEach(r => {
                    const kommune = getKommunerForQuery(omraderQueries, kommuner)?.find(
                        f => f?.nummer === r?.kommune
                    );
                    if (kommune) {
                        results.push(
                            new VegobjekterStatistic(r.antall, r.lengde, kommune)
                        );
                    }
                });
            });
    }

    return results;
};

/*
 * Fetch statistics for Vegobjekttype (VOT)
 */
export const fetchVegobjekterStatisticsV4 = async (
    typeId: number,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    vegobjekterQueries: VegobjekterQueries,
    integration: Integration,
    egenskapsfilters: Egenskapsfilter[] | null = null,
    vegsystemreferanser: string[] = [],
    timestamp: Date | null = new Date(),
    signal?: AbortSignal
): Promise<VegobjekterStatistic> => {
    // Prepare adjustments to search queries if they exist.
    const adjustedOmraderQueries = omraderQueries.withVegsystemreferanser(
        vegsystemreferanser.length > 0
            ? vegsystemreferanser
            : omraderQueries.vegsystemreferanse
    );
    const adjustedVegobjekterQueries = vegobjekterQueries.withKategorifilter(
        egenskapsfilters ?? vegobjekterQueries.kategorifilter
    );

    const { antall, lengde } = await integration.server.getVegobjekterStatistics(
        typeId,
        mapQueries,
        adjustedOmraderQueries,
        vegnettQueries,
        adjustedVegobjekterQueries,
        timestamp,
        signal
    );
    return { antall, lengde };
};

const hashFilter = (filters: Egenskapsfilter[]) =>
    filters
        .map(f => `${f.egenskapstype.id}:${f.operator}:${f.verdiToString()}`)
        .join('|');

export interface CategorizedVegobjekterV4 {
    vegobjekter: Vegobjekt[];
    lengde: number;
}

function addToCategory(category: CategorizedVegobjekterV4, vegobjekt: Vegobjekt): void {
    category.vegobjekter.push(vegobjekt);
    category.lengde += vegobjekt.lokasjon.strekningslengde || 0;
}

function findIntervalCategory(value: number, upperBounds: number[]): number | null {
    let lower = 0;
    let upper = upperBounds.length;

    while (lower < upper) {
        const middle = Math.floor((lower + upper) / 2);
        if (value <= upperBounds[middle]) upper = middle;
        else lower = middle + 1;
    }

    return lower < upperBounds.length || value > upperBounds[upperBounds.length - 1]
        ? lower
        : null;
}

export function categorizeVegobjekterV4(
    vegobjekter: Vegobjekt[],
    categoryType: Egenskapstype,
    egenskapsFilters: Egenskapsfilter[][]
): CategorizedVegobjekterV4[] {
    const categories = egenskapsFilters.map(() => ({ vegobjekter: [], lengde: 0 }));
    const categoryId = categoryType.id;

    if (categoryType.erEnum()) {
        const noValueKey = '__NO_VALUE__';
        const indexesByValue = new Map<string, number>();

        egenskapsFilters.forEach((filters, index) => {
            const filter = filters[0];
            const value =
                filter.operator === OperatorE.NO_VALUE
                    ? noValueKey
                    : filter.verdi instanceof EgenskapTillattVerdi
                      ? filter.verdi.verdi?.toString()
                      : filter.verdi?.toString();
            if (value !== undefined) indexesByValue.set(value, index);
        });

        vegobjekter.forEach(vegobjekt => {
            const value = vegobjekt.egenskaper?.find(
                egenskap => egenskap.id === categoryId
            )?.verdi;
            const key =
                value === null || value === undefined ? noValueKey : value.toString();
            const index = indexesByValue.get(key);
            if (index !== undefined) addToCategory(categories[index], vegobjekt);
        });

        return categories;
    }

    const upperBounds = egenskapsFilters
        .slice(0, -2)
        .map(filters => filters.at(-1)?.verdi as number);
    const noValueIndex = egenskapsFilters.length - 1;

    vegobjekter.forEach(vegobjekt => {
        const value = vegobjekt.egenskaper?.find(
            egenskap => egenskap.id === categoryId
        )?.verdi;
        if (value === null || value === undefined) {
            addToCategory(categories[noValueIndex], vegobjekt);
            return;
        }

        const index = findIntervalCategory(Number(value), upperBounds);
        if (index !== null) addToCategory(categories[index], vegobjekt);
    });

    return categories;
}

const fetchVegobjekterByCategories = async (
    vegobjekttypeState: VegobjekttypeState,
    commonQueries: CommonQueries,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    vegobjekterQueries: VegobjekterQueries,
    integration: Integration,
    getState: GetState,
    vegsystemreferanser: string[],
    timeBeforeFetch: number,
    signal?: AbortSignal
): Promise<VegobjekttypeCategoryState[]> => {
    const categoryType = vegobjekttypeState.category;
    if (categoryType === null) {
        throw new Error('Vegobjekttype mangler kategoriegenskap');
    }
    const typeId = vegobjekttypeState.typeId;
    if (typeId === null) {
        throw new Error('Vegobjekttype mangler type-id');
    }
    let egenskapsFilters: Egenskapsfilter[][];
    const visibilities = (vegobjekttypeState.categoryStates ?? []).reduce<
        Record<string, boolean>
    >((acc, { filters, visible }) => {
        acc[hashFilter(filters)] = visible;
        return acc;
    }, {});
    const colorIndexes = (vegobjekttypeState.categoryStates ?? []).reduce<
        Record<string, ColorIndex | null>
    >((acc, { filters, color }) => {
        acc[hashFilter(filters)] = color;
        return acc;
    }, {});
    if (categoryType.erEnum()) {
        // Categorize by enum //
        egenskapsFilters = categoryType.tillatte_verdier
            .map(
                tillattVerdi =>
                    new Egenskapsfilter(categoryType, OperatorE.EQ, tillattVerdi)
            )
            // Use a special case instead of null - this is the "no value" category
            .concat(new Egenskapsfilter(categoryType, OperatorE.NO_VALUE))
            .map(filter => [filter]);
    } else {
        const intervals = [...vegobjekttypeState.intervals].sort(numericSortComparator);
        egenskapsFilters = [
            [new Egenskapsfilter(categoryType, OperatorE.LEQ, intervals[0])],
        ];

        for (let i = 1; i < intervals.length; i++) {
            egenskapsFilters.push([
                new Egenskapsfilter(categoryType, OperatorE.GT, intervals[i - 1]),
                new Egenskapsfilter(categoryType, OperatorE.LEQ, intervals[i]),
            ]);
        }
        egenskapsFilters.push([
            new Egenskapsfilter(
                categoryType,
                OperatorE.GT,
                intervals[intervals.length - 1]
            ),
        ]);
        egenskapsFilters.push([new Egenskapsfilter(categoryType, OperatorE.NO_VALUE)]);
    }

    // Fetch vegobjekter and per-category total statistics in parallel — neither depends on the other.
    const needsPerCategoryTotals =
        omraderQueries.areasLength() > 0 || vegsystemreferanser.length > 0;
    const totalMapQueries =
        omraderQueries.areasLength() > 0 ? new MapQueries() : mapQueries;

    const statsPromise: Promise<(VegobjekterStatistic | null)[] | null> =
        needsPerCategoryTotals
            ? categoryType.erEnum()
                ? integration.server
                      .getVegobjekterStatisticsGruppertByEgenskap(
                          typeId,
                          categoryType.id,
                          totalMapQueries,
                          omraderQueries,
                          vegnettQueries,
                          vegobjekterQueries,
                          undefined,
                          signal
                      )
                      .then(gruppertResult => {
                          const gruppertMap = new Map<
                              string | null,
                              VegobjekterStatistic
                          >();
                          for (const item of gruppertResult) {
                              const key =
                                  item.egenskapsVerdi !== null
                                      ? item.egenskapsVerdi.toString()
                                      : null;
                              gruppertMap.set(key, {
                                  antall: item.antall,
                                  lengde: item.lengde,
                              });
                          }
                          return egenskapsFilters.map(filters => {
                              const op = filters[0].operator;
                              if (op === OperatorE.NO_VALUE && filters.length === 1) {
                                  return (
                                      gruppertMap.get(null) ?? { antall: 0, lengde: 0 }
                                  );
                              } else if (op === OperatorE.EQ && filters.length === 1) {
                                  const key =
                                      filters[0].verdi instanceof EgenskapTillattVerdi
                                          ? filters[0].verdi.id.toString()
                                          : (filters[0].verdi?.toString() ?? null);
                                  return gruppertMap.get(key) ?? { antall: 0, lengde: 0 };
                              }
                              return null;
                          });
                      })
                : Promise.allSettled(
                      egenskapsFilters.map(filters =>
                          fetchVegobjekterStatisticsV4(
                              typeId,
                              totalMapQueries,
                              omraderQueries,
                              vegnettQueries,
                              vegobjekterQueries,
                              integration,
                              filters,
                              [], // no vegsystemreferanser restriction for the total count
                              undefined,
                              signal
                          )
                      )
                  ).then(results =>
                      results.map(r => (r.status === 'fulfilled' ? r.value : null))
                  )
            : Promise.resolve(null);

    const [vegobjekter, perCategoryTotalStats] = await Promise.all([
        fetchVegobjekterRecursively(
            typeId,
            commonQueries,
            mapQueries,
            omraderQueries,
            vegnettQueries,
            vegobjekterQueries.withInkluder([
                ...vegobjekterQueries.inkluder,
                'egenskaper',
            ]),
            integration,
            getState,
            null,
            vegsystemreferanser,
            timeBeforeFetch,
            signal
        ),
        statsPromise,
    ]);

    const categorizedVegobjekter = categorizeVegobjekterV4(
        vegobjekter,
        categoryType,
        egenskapsFilters
    );

    return egenskapsFilters.map((filters, i) => {
        const { vegobjekter: filtered, lengde } = categorizedVegobjekter[i];

        const vegobjekterResult = new VegobjekterResult(
            filtered,
            perCategoryTotalStats?.[i] ?? undefined,
            {
                antall: filtered.length,
                lengde: Number.parseFloat(lengde.toFixed(3)),
            }
        );

        const filterHash = hashFilter(filters);
        const visible = filterHash in visibilities ? visibilities[filterHash] : true;
        let colorIndex: ColorIndex | null = null;

        if (filterHash in colorIndexes) {
            colorIndex = colorIndexes[filterHash];
        }

        if (colorIndex === null && vegobjekterResult.vegobjekter.length > 0) {
            colorIndex = resolveInitialCategoryColor(
                typeId,
                filters,
                integration.colorHandler
            );
        } else if (colorIndex !== null && vegobjekterResult.vegobjekter.length === 0) {
            integration.colorHandler.decrementColor(colorIndex);
            colorIndex = null;
        }

        return new VegobjekttypeCategoryState(
            categoryType.erEnum() ? CategoryType.ENUM : CategoryType.INTERVAL,
            filters,
            colorIndex,
            vegobjekterResult,
            false,
            visible
        );
    });
};

const fetchVegobjekterByOmradeType = async (
    typeId: number,
    commonQueries: CommonQueries,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    vegobjekterQueries: VegobjekterQueries,
    integration: Integration,
    getState: GetState,
    egenskapsfilters: Egenskapsfilter[] | null = null,
    vegsystemreferanser: string[] = [],
    timeBeforeFetch = 0,
    signal?: AbortSignal
): Promise<Vegobjekt[]> => {
    let uniqueVegobjekter: Set<Vegobjekt> = new Set<Vegobjekt>();

    if (omraderQueries.areasLength() > 0) {
        const omradeTypeLenker: Vegobjekt[] = await fetchVegobjekterRecursively(
            typeId,
            commonQueries,
            mapQueries,
            omraderQueries,
            vegnettQueries,
            vegobjekterQueries,
            integration,
            getState,
            egenskapsfilters,
            vegsystemreferanser,
            timeBeforeFetch,
            signal
        );
        uniqueVegobjekter = new Set<Vegobjekt>([
            ...uniqueVegobjekter,
            ...omradeTypeLenker,
        ]);
        return Array.from(uniqueVegobjekter);
    } else {
        return await fetchVegobjekterRecursively(
            typeId,
            commonQueries,
            mapQueries,
            omraderQueries,
            vegnettQueries,
            vegobjekterQueries,
            integration,
            getState,
            egenskapsfilters,
            vegsystemreferanser,
            timeBeforeFetch,
            signal
        );
    }
};

const fetchVegobjekterRecursively = async (
    typeId: number,
    commonQueries: CommonQueries,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    vegobjekterQueries: VegobjekterQueries,
    integration: Integration,
    getState: GetState,
    egenskapsfilters: Egenskapsfilter[] | null = null,
    vegsystemreferanser: string[] = [],
    timeBeforeFetch: number,
    signal?: AbortSignal
): Promise<Vegobjekt[]> => {
    // Check base search for any change in parameters. Abort if there are any.
    if (
        hasSearchStateChanged(
            typeId,
            mapQueries,
            omraderQueries,
            vegnettQueries,
            vegobjekterQueries,
            getState,
            integration.data.kommuner
        )
    ) {
        log('Search query has changed, aborting old search');
        const actionType = ActionType.VEGOBJEKTER_FETCH_ABORTED;
        const timeAfterFetch = new Date().getTime();
        integration.server.postPerformanceLogEntry(
            actionType,
            timeAfterFetch - timeBeforeFetch,
            {
                vegobjekttype: typeId,
            }
        );
        return [];
    }
    const timestamp = getState().searchState.searchDropdownState.timestamp;
    // Prepare adjustments to search queries if they exist.
    const adjustedOmraderQueries =
        vegsystemreferanser.length > 0
            ? omraderQueries.withVegsystemreferanser(vegsystemreferanser)
            : null;
    const kategorisertVegobjekterQueries = egenskapsfilters
        ? vegobjekterQueries.withKategorifilter(egenskapsfilters)
        : null;
    // Start recursion.
    const fragment = await integration.server.getVegobjekter(
        typeId,
        commonQueries,
        mapQueries,
        adjustedOmraderQueries ?? omraderQueries,
        vegnettQueries,
        kategorisertVegobjekterQueries ?? vegobjekterQueries,
        timestamp,
        signal
    );

    if (
        fragment.metadata.returnert < fragment.metadata.sidestorrelse ||
        !fragment.metadata.neste
    ) {
        return fragment.objekter;
    }

    // Continue recursion if we haven't reached the end page.
    return fragment.objekter.concat(
        await fetchVegobjekterRecursively(
            typeId,
            commonQueries.withStart(fragment.metadata.neste.start),
            mapQueries,
            omraderQueries,
            vegnettQueries,
            vegobjekterQueries,
            integration,
            getState,
            egenskapsfilters,
            vegsystemreferanser,
            timeBeforeFetch,
            signal
        )
    );
};

const hasSearchStateChanged = (
    typeId: number,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    vegobjekterQueries: VegobjekterQueries,
    getState: GetState,
    kommuner: Kommune[]
): boolean => {
    return (
        JSON.stringify(stateMap(getState()).toQueries()) !== JSON.stringify(mapQueries) ||
        JSON.stringify(stateOmrader(getState()).toOmradeQueries(kommuner)) !==
            JSON.stringify(omraderQueries) ||
        JSON.stringify(stateVegnett(getState()).toQueries()) !==
            JSON.stringify(vegnettQueries) ||
        JSON.stringify(
            stateVegobjekter(getState()).typeToQueries(typeId).withInkluder([])
        ) !== JSON.stringify(vegobjekterQueries.withInkluder([]))
    );
};
